import { IField, ISurvey } from '../../constants/types';
import { AmendmentChange, AmendmentEntry, AmendmentValue, PhotoPlacement } from './AmendmentHistory';

/**
 * SKIN-602 — turns a submission's edit history, as the API serves it
 * (`GET .../submission/:idSubmission/edit-history`), into AmendmentHistory
 * entries: field labels from the survey, option values as their labels, and
 * encrypted values decrypted here in the browser (the API never can).
 */

/** Where a SKIN-576 photo correction put the photo, as the API records it. */
export type StoredPlacement = 'MOVE_FROM' | 'MOVE_TO' | 'ATTACH_TO' | 'SUPERSEDED_COPY';

export interface SubmissionEditChangeItem {
    fieldId: number;
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    instanceNumber?: number;
    fieldGroupName?: string;
    placement?: StoredPlacement;
}

export interface SubmissionEditHistoryRow {
    id: number;
    action: 'EDIT' | 'DELETE';
    editedByName: string;
    editedAt: string;
    editReason: string;
    changes: SubmissionEditChangeItem[];
}

/** Decrypts one stored ciphertext, or answers null when this browser cannot. */
export type DecryptValue = (ciphertext: string) => string | null;

/**
 * SUPERSEDED_COPY is the moved photo stripped from an older autosave copy of
 * the same visit: bookkeeping that keeps QC from resurrecting the old placement,
 * not a second correction. The MOVE_FROM/MOVE_TO pair already tells the story.
 */
const PHOTO_PLACEMENT: Record<Exclude<StoredPlacement, 'SUPERSEDED_COPY'>, PhotoPlacement> = {
    MOVE_FROM: 'MOVED_OUT',
    MOVE_TO: 'MOVED_IN',
    ATTACH_TO: 'ATTACHED',
};

/** Every field of the survey by id: section fields, group fields and the subform fields an option opens. */
export function surveyFieldsById(survey: ISurvey): Map<number, IField> {
    const byId = new Map<number, IField>();
    const visit = (fields: IField[] = []) => {
        for (const field of fields) {
            byId.set(field.id, field);
            for (const option of field.options ?? []) {
                visit(option.subformGroup?.fields);
            }
        }
    };
    for (const section of survey.sections ?? []) {
        visit(section.fields);
        for (const group of section.fieldGroups ?? []) visit(group.fields);
    }
    return byId;
}

function optionLabel(field: IField | undefined, value: unknown): string {
    const text = String(value);
    return field?.options?.find((option) => String(option.value) === text)?.label ?? text;
}

/**
 * A stored value as text. Multi-value fields are stored as JSON arrays, of
 * plain values or of `{ multioption }` objects; anything else is shown as stored.
 */
export function displayText(raw: string, field: IField | undefined): string {
    if (raw.trimStart().startsWith('[')) {
        try {
            const items: Array<string | number | null | { multioption: string }> = JSON.parse(raw);
            return items.map((item) => optionLabel(field, item !== null && typeof item === 'object' ? item.multioption : item)).join(', ');
        } catch {
            return raw;
        }
    }
    return optionLabel(field, raw);
}

function toValue(raw: string | null, field: IField | undefined, decrypt: DecryptValue): AmendmentValue {
    if (raw === null || raw === '') return { kind: 'empty' };
    if (!field?.encrypted) return { kind: 'text', text: displayText(raw, field) };
    const plain = decrypt(raw);
    return plain === null ? { kind: 'encrypted' } : { kind: 'text', text: displayText(plain, field) };
}

function toChange(item: SubmissionEditChangeItem, fields: Map<number, IField>, decrypt: DecryptValue): AmendmentChange | null {
    if (item.placement === 'SUPERSEDED_COPY') return null;
    const field = fields.get(item.fieldId);
    const label = field?.label || item.fieldName;
    const context = item.instanceNumber !== undefined && item.fieldGroupName
        ? `${item.fieldGroupName} #${item.instanceNumber}`
        : undefined;
    if (item.placement) {
        return { kind: 'photo', label, context, placement: PHOTO_PLACEMENT[item.placement] };
    }
    return {
        kind: 'value', label, context,
        oldValue: toValue(item.oldValue, field, decrypt),
        newValue: toValue(item.newValue, field, decrypt),
    };
}

export function toAmendmentEntries(rows: SubmissionEditHistoryRow[], survey: ISurvey, decrypt: DecryptValue): AmendmentEntry[] {
    const fields = surveyFieldsById(survey);
    return rows.map((row) => ({
        id: row.id,
        action: row.action,
        actorName: row.editedByName,
        at: row.editedAt,
        reason: row.editReason,
        changes: row.changes
            .map((item) => toChange(item, fields, decrypt))
            .filter((change): change is AmendmentChange => change !== null),
    }));
}
